import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Package, Users, BarChart3, Clock, Wallet } from 'lucide-react';
import StatCard from '../../components/admin/StatCard';
import ErrorState from '../../components/common/ErrorState';
import { Skeleton } from '../../components/common/Skeleton';
import { fetchObituaries, fetchOrders, fetchUsers, fetchWreaths } from '../../utils/api';
import { formatCurrency } from '../../utils/formatters';
import { ORDER_STATUS, getOrderStatusLabel, getOrderStatusBadgeClass } from '../../utils/constants';

// KST(UTC+9) 기준 YYYY-MM-DD 문자열 — UTC 자정 경계 오차 보정
const toKstDateStr = (isoString) => {
  const d = new Date(isoString);
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().split('T')[0];
};

const periodOptions = [
  { key: 'all', label: '전체' },
  { key: 'today', label: '오늘' },
  { key: 'week', label: '최근 7일' },
  { key: 'month', label: '최근 30일' },
];

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [orders, setOrders] = useState([]);
  const [obituaries, setObituaries] = useState([]);
  const [users, setUsers] = useState([]);
  const [wreaths, setWreaths] = useState([]);
  const [period, setPeriod] = useState('all');

  const loadDashboardData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [obituariesData, ordersData, usersData, wreathsData] = await Promise.all([
        fetchObituaries(),
        fetchOrders(),
        fetchUsers(),
        fetchWreaths(),
      ]);
      // 빈 배열만 돌아오는 동시 실패 케이스 → 에러로 간주
      if (
        ordersData.length === 0 &&
        obituariesData.length === 0 &&
        usersData.length === 0
      ) {
        setError(true);
        return;
      }
      setObituaries(obituariesData);
      setOrders(ordersData);
      setUsers(usersData);
      setWreaths(wreathsData);
    } catch (e) {
      console.error('대시보드 데이터 로드 실패:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // 기간 필터된 주문
  const filteredOrders = useMemo(() => {
    if (period === 'all') return orders;
    const now = new Date();
    const todayKst = toKstDateStr(now.toISOString());
    return orders.filter((o) => {
      if (!o.orderedAt) return false;
      const orderDate = toKstDateStr(o.orderedAt);
      if (period === 'today') return orderDate === todayKst;
      const days = period === 'week' ? 7 : 30;
      const diff = (new Date(todayKst) - new Date(orderDate)) / (1000 * 60 * 60 * 24);
      return diff >= 0 && diff < days;
    });
  }, [orders, period]);

  // 통계
  const stats = useMemo(() => {
    const todayKst = toKstDateStr(new Date().toISOString());
    const todayOrders = orders.filter((o) => o.orderedAt && toKstDateStr(o.orderedAt) === todayKst);
    const pendingOrders = orders.filter(
      (o) => o.orderStatus === 'pending' || o.orderStatus === 'preparing'
    );
    const totalRevenue = orders
      .filter((o) => o.orderStatus === 'delivered')
      .reduce((sum, o) => sum + (o.totalAmount || 0), 0);

    return {
      totalObituaries: obituaries.length,
      totalOrders: orders.length,
      totalUsers: users.length,
      todayOrders: todayOrders.length,
      pendingOrders: pendingOrders.length,
      totalRevenue,
    };
  }, [orders, obituaries, users]);

  // 주문 상태 분포(도넛)
  const statusDistribution = useMemo(() => {
    const counts = {};
    filteredOrders.forEach((o) => {
      const s = o.orderStatus || 'pending';
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.keys(ORDER_STATUS)
      .filter((s) => counts[s])
      .map((s) => ({
        key: s,
        label: getOrderStatusLabel(s),
        count: counts[s],
        color: ORDER_STATUS[s].badge,
      }));
  }, [filteredOrders]);

  // 카테고리별 판매 수량(막대) — 주문 items 의 wreathId → 카테고리 매핑
  const categorySales = useMemo(() => {
    if (wreaths.length === 0) return [];
    const wreathMap = new Map(wreaths.map((w) => [w.id, w]));
    const counts = {};
    filteredOrders.forEach((o) => {
      (o.items || []).forEach((item) => {
        const w = wreathMap.get(item.wreathId);
        const cat = w?.category || '기타';
        counts[cat] = (counts[cat] || 0) + (item.quantity || 1);
      });
    });
    return Object.entries(counts)
      .map(([category, qty]) => ({ category, qty }))
      .sort((a, b) => b.qty - a.qty);
  }, [filteredOrders, wreaths]);

  // 도넛 차트용 색상(SVG는 클래스 stroke 사용 어려워 hex 매핑)
  const donutColors = {
    pending: '#3b82f6',
    preparing: '#eab308',
    shipping: '#a855f7',
    delivered: '#22c55e',
    cancelled: '#ef4444',
  };

  const recentOrders = useMemo(
    () =>
      [...orders]
        .sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt))
        .slice(0, 5),
    [orders]
  );

  if (error) {
    return (
      <div className="p-6">
        <h1 className="mb-6 text-3xl font-bold text-gray-900">대시보드</h1>
        <ErrorState
          title="대시보드 데이터를 불러오지 못했습니다"
          message="네트워크 상태를 확인한 뒤 다시 시도해주세요."
          onRetry={loadDashboardData}
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold text-gray-900">대시보드</h1>
        {/* 기간 필터 */}
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
          {periodOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setPeriod(opt.key)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                period === opt.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <StatCard title="전체 부고" value={stats.totalObituaries} icon={FileText} color="blue" loading={loading}
          onClick={() => navigate('/admin/obituaries')} />
        <StatCard title="전체 주문" value={stats.totalOrders} icon={Package} color="green" loading={loading}
          onClick={() => navigate('/admin/orders')} />
        <StatCard title="전체 회원" value={stats.totalUsers} icon={Users} color="gray" loading={loading}
          onClick={() => navigate('/admin/users')} />
        <StatCard title="오늘 주문" value={stats.todayOrders} icon={BarChart3} color="yellow" loading={loading}
          trend="(KST 기준)" />
        <StatCard title="처리 대기" value={stats.pendingOrders} icon={Clock} color="red" loading={loading}
          onClick={() => navigate('/admin/orders')} trend="클릭하여 주문 관리로" />
        <StatCard title="총 매출(배송완료)" value={formatCurrency(stats.totalRevenue)} icon={Wallet} color="green" loading={loading} />
      </div>

      {/* 차트 영역 */}
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* 주문 상태 분포 도넛 */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-bold text-gray-900">주문 상태 분포</h2>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : statusDistribution.length === 0 ? (
            <p className="py-8 text-center text-gray-500">표시할 주문이 없습니다.</p>
          ) : (
            <DonutChart data={statusDistribution} colors={donutColors} />
          )}
        </div>

        {/* 카테고리별 판매 막대 */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-lg font-bold text-gray-900">카테고리별 판매 수량</h2>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : categorySales.length === 0 ? (
            <p className="py-8 text-center text-gray-500">판매 데이터가 없습니다.</p>
          ) : (
            <BarChart data={categorySales} />
          )}
        </div>
      </div>

      {/* 최근 주문 */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">최근 주문</h2>
          <button
            type="button"
            onClick={() => navigate('/admin/orders')}
            className="text-sm font-medium text-primary-600 hover:text-primary-800"
          >
            전체 보기 →
          </button>
        </div>
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : recentOrders.length === 0 ? (
          <p className="py-8 text-center text-gray-500">최근 주문이 없습니다.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-700">주문번호</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-700">주문자</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-700">금액</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-700">상태</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-700">주문일</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentOrders.map((order) => (
                  <tr
                    key={order.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate('/admin/orders')}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{order.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{order.senderName}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(order.totalAmount)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${getOrderStatusBadgeClass(order.orderStatus)}`}>
                        {getOrderStatusLabel(order.orderStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{toKstDateStr(order.orderedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// === 순수 SVG 도넛 차트 ===
const DonutChart = ({ data, colors }) => {
  const total = data.reduce((sum, d) => sum + d.count, 0);
  const radius = 70;
  const stroke = 28;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-around">
      <svg width="180" height="180" viewBox="0 0 180 180" role="img" aria-label="주문 상태 분포 도넛 차트">
        <g transform="rotate(-90 90 90)">
          {data.map((d) => {
            const fraction = d.count / total;
            const dash = fraction * circumference;
            const seg = (
              <circle
                key={d.key}
                cx="90"
                cy="90"
                r={radius}
                fill="none"
                stroke={colors[d.key] || '#9ca3af'}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return seg;
          })}
        </g>
        <text x="90" y="84" textAnchor="middle" className="fill-gray-900 text-2xl font-bold">
          {total}
        </text>
        <text x="90" y="104" textAnchor="middle" className="fill-gray-500 text-xs">
          건
        </text>
      </svg>
      <ul className="space-y-2">
        {data.map((d) => (
          <li key={d.key} className="flex items-center gap-2 text-sm">
            <span
              className="inline-block h-3 w-3 rounded-sm"
              style={{ backgroundColor: colors[d.key] || '#9ca3af' }}
            />
            <span className="text-gray-700">{d.label}</span>
            <span className="font-semibold text-gray-900">{d.count}</span>
            <span className="text-gray-400">({Math.round((d.count / total) * 100)}%)</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// === 순수 CSS 막대 차트 ===
const BarChart = ({ data }) => {
  const max = Math.max(...data.map((d) => d.qty), 1);
  return (
    <div className="space-y-3 py-2">
      {data.map((d) => (
        <div key={d.category}>
          <div className="mb-1 flex justify-between text-sm">
            <span className="text-gray-700">{d.category}</span>
            <span className="font-semibold text-gray-900">{d.qty}개</span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-primary-600 transition-all duration-500"
              style={{ width: `${(d.qty / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default DashboardPage;
